import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
  Injectable,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { appRoutes } from './app.routes';
import {
  ENVIRONMENT_CONFIG,
  authInterceptor,
  errorInterceptor,
  mockInterceptor,
} from '@victory-lib-ui/shared/data-access';
import { environment } from '../environments/environment';
import { provideTransloco } from '@jsverse/transloco';
import { translocoConfig } from '@jsverse/transloco';
import { TranslocoLoader } from '@jsverse/transloco';
import { catchError, forkJoin, map, of } from 'rxjs';

type TranslationPrimitive = string | number | boolean | null;
type TranslationNode =
  | TranslationPrimitive
  | TranslationNode[]
  | TranslationMap;

interface TranslationMap {
  [key: string]: TranslationNode;
}

@Injectable({ providedIn: 'root' })
class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  private isMergeableObject(value: unknown): value is TranslationMap {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private deepMerge(
    target: TranslationMap,
    source: TranslationMap
  ): TranslationMap {
    const merged: TranslationMap = { ...target };

    Object.entries(source).forEach(([key, sourceValue]) => {
      const targetValue = merged[key];

      if (
        this.isMergeableObject(targetValue) &&
        this.isMergeableObject(sourceValue)
      ) {
        merged[key] = this.deepMerge(targetValue, sourceValue);
      } else {
        merged[key] = sourceValue;
      }
    });

    return merged;
  }

  getTranslation(lang: string) {
    const emptyTranslation: TranslationMap = {};
    const paths = [
      `/assets/i18n/${lang}.json`,
      `/assets/i18n/${lang}/common.json`,
      `/assets/i18n/${lang}/auth.json`,
      `/assets/i18n/${lang}/dashboard.json`,
      `/assets/i18n/${lang}/passage.json`,
      `/assets/i18n/${lang}/passage_theme.json`,
      `/assets/i18n/${lang}/theme.json`,
      `/assets/i18n/${lang}/search.json`,
      `/assets/i18n/${lang}/audio.json`,
      `/assets/i18n/${lang}/dictionary.json`,
      `/assets/i18n/${lang}/layout.json`,
      `/assets/i18n/${lang}/settings.json`,
      `/assets/i18n/${lang}/toast.json`,
      `/assets/i18n/${lang}/bible.json`,
    ];

    return forkJoin(
      paths.map((path) =>
        this.http.get<TranslationMap>(path).pipe(catchError(() => of({})))
      )
    ).pipe(
      map((translations) =>
        translations.reduce(
          (acc, current) => this.deepMerge(acc, current),
          emptyTranslation
        )
      )
    );
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENVIRONMENT_CONFIG, useValue: environment },

    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),
    provideHttpClient(
      withInterceptors([mockInterceptor, authInterceptor, errorInterceptor])
    ),
    provideTransloco({
      config: translocoConfig({
        availableLangs: ['en'],
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: environment.production,
      }),
      loader: TranslocoHttpLoader,
    }),
  ],
};
